"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { ArrowRightIcon } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { SIGN_OUT_REASON_KEY } from "@/lib/axios";
import { CDN_ASSETS } from "@/lib/cdn";
import { AppDispatch } from "@/store/store";
import { OtpRequestError, requestLoginOtp, verifyLoginOtp } from "@/store/slices/authSlice";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

// The soft field shell. These override the shadcn defaults through the `cn()`
// call inside each component, so the last conflicting class wins.
//
// No focus-visible or aria-invalid border/ring recolor — the Input strips
// all of that and keeps the border the
// same color always. `--brand` itself is a red-orange hue, so the old
// focus-visible:border-brand read as the same "red highlight" the invalid
// state used, just on click instead of on error.
const FIELD_SHELL =
  "h-11 rounded-xl border-border bg-muted/45 shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:border-border aria-invalid:border-border aria-invalid:ring-0 dark:aria-invalid:ring-0";

export default function LoginPage() {
  const dispatch = useDispatch<AppDispatch>();
  const [step, setStep] = React.useState<"email" | "otp">("email");
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  // The email the server last answered account_not_found for. Kept apart from
  // emailError, which onBlur resets — blurring the field to click the Sign up
  // link would otherwise remove the link mid-click. Editing the email hides it.
  const [notFoundEmail, setNotFoundEmail] = React.useState("");
  const showNotFound = !!notFoundEmail && notFoundEmail === email && !emailError;
  const [otp, setOtp] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const otpRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Why they were sent here, if they did not arrive on their own — a disabled
  // account being the case that matters, since otherwise the app would just stop
  // working with nothing said. Read once and cleared, so it does not reappear.
  React.useEffect(() => {
    const reason = window.sessionStorage.getItem(SIGN_OUT_REASON_KEY);
    if (!reason) return;
    window.sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
    toast.error(reason);
  }, []);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  function validateEmail(value: string) {
    if (!value.trim()) return "Email is required";
    if (!EMAIL_REGEX.test(value)) return "Enter a valid email address";
    return "";
  }

  async function sendOtp(event: React.FormEvent) {
    event.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }

    setLoading(true);
    setNotFoundEmail("");
    try {
      await dispatch(requestLoginOtp({ email })).unwrap();
      toast.success("A verification code has been sent to your email");
      setOtp("");
      setStep("otp");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      const { message, code } = (error ?? {}) as OtpRequestError;
      // Same as fnx: an unknown email stays on this step with a pointer to
      // sign up, rather than moving on to wait for a code that never comes.
      if (code === "account_not_found") {
        setNotFoundEmail(email);
      } else {
        toast.error(message || "Unable to send verification code");
      }
    } finally {
      setLoading(false);
    }
  }

  async function submitOtp(event: React.FormEvent) {
    event.preventDefault();
    if (otp.length !== OTP_LENGTH) return;

    setLoading(true);
    try {
      await dispatch(verifyLoginOtp({ email, otp })).unwrap();
      toast.success("Signed in successfully");
      // A document request, not router.replace(), because the session cookie
      // only came into existence on the line above.
      //
      // /home builds static, so the App Router cached its RSC payload when
      // this page prefetched it moments ago — while logged out, when proxy.ts
      // answered with a redirect back to /auth/login. staleTimes.static is
      // 300s, so router.replace() would replay that cached redirect for five
      // minutes and strand the user on this screen with a valid session. It is
      // why pressing refresh "fixed" the login: a document request skips the
      // Router Cache and re-runs the middleware with the new cookies. This
      // does that deliberately instead of leaving it to the user.
      //
      // replace(), not assign() — otherwise the browser's back button returns
      // to this now-stale login screen instead of leaving the authenticated app.
      window.location.replace("/home");
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Invalid or expired code");
    } finally {
      setLoading(false);
    }
  }

  async function resendOtp() {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await dispatch(requestLoginOtp({ email })).unwrap();
      toast.success("A new verification code has been sent");
      setOtp("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      toast.error((error as OtpRequestError | undefined)?.message || "Unable to resend code");
    } finally {
      setLoading(false);
    }
  }

  function handleOtpChange(value: string, index: number) {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = otp.split("");
    next[index] = digit;
    const joined = next.join("").slice(0, OTP_LENGTH);
    setOtp(joined);
    if (digit && index < OTP_LENGTH - 1) otpRefs.current[index + 1]?.focus();
  }

  function handleOtpKeyDown(event: React.KeyboardEvent<HTMLInputElement>, index: number) {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  }

  // Each box has maxLength=1, so a browser paste gets truncated to one
  // character before onChange ever sees it — this has to intercept the
  // clipboard event directly to spread the full code across the boxes.
  function handleOtpPaste(event: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    setOtp(pasted);
    otpRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  return (
    <div className="w-full max-w-sm bg-white">
      <div className="flex flex-col items-center text-center">
        <Image
          src={CDN_ASSETS.logo}
          alt="Infynd"
          width={202}
          height={93}
          priority
          className="h-9 w-auto sm:h-10"
        />

        {step === "email" ? (
          <div>
            <h2 className="text-[28px] leading-tight font-medium tracking-tight sm:text-[34px]">
              Welcome back
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Enter your email — we&apos;ll send you a one-time code to sign in.
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-[28px] leading-tight font-medium tracking-tight sm:text-[34px]">
              Enter your code
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              We sent a {OTP_LENGTH}-digit code to <span className="font-medium text-foreground">{email}</span>.
            </p>
          </div>
        )}
      </div>

      {step === "email" ? (
        <>
          <form onSubmit={sendOtp}>
            <FieldGroup className="mt-6 gap-4">
              <Field data-invalid={!!emailError || showNotFound || undefined}>
                <FieldLabel htmlFor="email" className="text-[13px] font-medium">
                  Email <span className="text-brand">*</span>
                </FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(validateEmail(e.target.value));
                  }}
                  onBlur={(e) => {
                    // Browser-autofill (password manager credential fill in
                    // particular) can set the field's DOM value without ever
                    // firing onChange, leaving `email`/`emailError` stuck on
                    // whatever was last typed - a real value the user picked
                    // from autofill then shows a permanent red border. Blur
                    // fires reliably regardless of how the value got there, so
                    // resync from the actual DOM value here as a backstop.
                    const value = e.target.value;
                    setEmail(value);
                    setEmailError(validateEmail(value));
                  }}
                  aria-invalid={!!emailError || showNotFound}
                  className={FIELD_SHELL}
                  required
                />
                <FieldError className="text-xs font-medium">
                  {showNotFound ? (
                    <>
                      No account found for this email.{" "}
                      <Link
                        href="/auth/register"
                        replace
                        className="font-medium text-brand underline-offset-4 hover:underline"
                      >
                        Sign up
                      </Link>{" "}
                      first.
                    </>
                  ) : (
                    emailError
                  )}
                </FieldError>
              </Field>

              <Button
                type="submit"
                disabled={loading}
                className="group h-11 w-full rounded-xl bg-brand text-white hover:bg-brand/90"
              >
                {loading ? (
                  <>
                    <Spinner />
                    Sending code…
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
            </FieldGroup>
          </form>
        </>
      ) : (
        <>
          <form onSubmit={submitOtp}>
            <FieldGroup className="mt-6 gap-4">
              <div className="grid grid-cols-6 gap-2">
                {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                  <Input
                    key={i}
                    ref={(el) => {
                      otpRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={otp[i] ?? ""}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                    onKeyDown={(e) => handleOtpKeyDown(e, i)}
                    onPaste={handleOtpPaste}
                    className="h-12 w-full rounded-xl px-0 text-center text-lg font-semibold"
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>

              <Button
                type="submit"
                disabled={loading || otp.length !== OTP_LENGTH}
                className="group h-11 w-full rounded-xl bg-brand text-white hover:bg-brand/90"
              >
                {loading ? (
                  <>
                    <Spinner />
                    Verifying…
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setStep("email")}
                  className="h-auto p-0 font-medium text-muted-foreground underline-offset-4 hover:underline"
                >
                  Change email
                </Button>
                <Button
                  type="button"
                  variant="link"
                  onClick={resendOtp}
                  disabled={cooldown > 0 || loading}
                  className="h-auto p-0 font-medium text-muted-foreground underline-offset-4 hover:underline disabled:text-muted-foreground/60 disabled:no-underline"
                >
                  {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
                </Button>
              </div>
            </FieldGroup>
          </form>
        </>
      )}

      <p className="mt-5 text-center text-sm text-muted-foreground">
        Don&apos;t have an account?{" "}
        {/* replace, not push — bouncing between login and register while
        deciding which to use shouldn't stack up history entries that
        later surface on repeated back-presses and get bounced forward
        again by proxy.ts once the person is actually signed in. */}
        <Link
          href="/auth/register"
          replace
          className="font-medium text-brand underline-offset-4 hover:underline"
        >
          Sign up
        </Link>
      </p>
    </div>
  );
}
